import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {BucketName, DocumentAttachment, ReferenceBookType} from '@npck/af-frontend-api';
import {Spinner} from '@npck/libs/commons';
import {DATE_TIME_FORMATS} from '../../../../../infra/date-time.adapter';
import {
    CONTACT_REASON_OTHER_MAX_LENGTH,
    CONTACT_TYPES_WITH_INLINE_FRAUDSTER_ROLE,
    CONTACT_TYPES_WITH_MSISDN,
    CONTACT_TYPES_WITH_SUSPICIOUS_LINKS,
    ContactForm,
    DETAILS_MAX_LENGTH,
    hasDetailsTextarea,
    OTHER_FIELD_MAX_LENGTH,
    OTHER_VALUE,
    VICTIM_ACTION_OTHER_MAX_LENGTH,
} from '../contact-details-step.component';

const CONTACT_TYPE_SECTIONS: Record<string, ContactTypeSection[]> = {
    CALL: [{
        dictionaryName: ReferenceBookType.CALL_CONTACT_TYPE,
        controlName: 'callContactType',
        otherControlName: 'callContactTypeOther',
        label: 'i18n_af_labelCallContactDetails',
        placeholder: 'i18n_af_placeholderCallContactDetails',
        otherLabel: 'i18n_af_labelCallContactTypeOther',
        otherPlaceholder: 'i18n_af_placeholderCallContactTypeOther',
    }],
    MESSENGER: [{
        dictionaryName: ReferenceBookType.MESSENGER_CONTACT_SUBTYPE,
        controlName: 'messengerContactSubtype',
        otherControlName: 'messengerContactSubtypeOther',
        label: 'i18n_af_labelMessengerContactDetails',
        placeholder: 'i18n_af_placeholderMessengerContactDetails',
        otherLabel: 'i18n_af_labelMessengerContactSubtypeOther',
        otherPlaceholder: 'i18n_af_placeholderMessengerContactSubtypeOther',
    }],
    EMAIL: [{
        dictionaryName: ReferenceBookType.EMAIL_CONTACT_TYPE,
        controlName: 'emailContactType',
        otherControlName: 'emailContactTypeOther',
        label: 'i18n_af_labelEmailContactDetails',
        placeholder: 'i18n_af_placeholderEmailContactDetails',
        otherLabel: 'i18n_af_labelEmailContactTypeOther',
        otherPlaceholder: 'i18n_af_placeholderEmailContactTypeOther',
    }],
    SOCIAL: [{
        dictionaryName: ReferenceBookType.SOCIAL_CONTACT_TYPE,
        controlName: 'socialContactType',
        otherControlName: 'socialContactTypeOther',
        label: 'i18n_af_labelSocialContactDetails',
        placeholder: 'i18n_af_placeholderSocialContactDetails',
        otherLabel: 'i18n_af_labelSocialContactTypeOther',
        otherPlaceholder: 'i18n_af_placeholderSocialContactTypeOther',
    }],
    INTERNET: [{
        dictionaryName: ReferenceBookType.INTERNET_CONTACT_TYPE,
        controlName: 'internetContactType',
        otherControlName: 'internetContactTypeOther',
        label: 'i18n_af_labelInternetContactDetails',
        placeholder: 'i18n_af_placeholderInternetContactDetails',
        otherLabel: 'i18n_af_labelInternetContactTypeOther',
        otherPlaceholder: 'i18n_af_placeholderInternetContactTypeOther',
    }],
};

@Component({
    selector: 'af-contact-details-form',
    templateUrl: './contact-details-form.component.html',
    styleUrl: './contact-details-form.component.scss',
    providers: [
        {provide: MAT_DATE_FORMATS, useValue: DATE_TIME_FORMATS},
    ],
    standalone: false,
})
export class ContactDetailsFormComponent {
    @Input() contactForm: FormGroup<ContactForm>;
    @Input() showAddContactButton: boolean;
    @Input() isAddContactButtonDisabled: boolean;
    @Input() msisdnPredefinedValue: string;
    @Input() msisdnReadOnly = false;
    @Input() existingAttachments: DocumentAttachment[];
    @Output() addNewContactListener = new EventEmitter<void>();
    @Output() addLinkListener = new EventEmitter<void>();
    @Output() removeLinkListener = new EventEmitter<number>();

    ReferenceBookType = ReferenceBookType;
    Spinner = Spinner;
    BucketName = BucketName;
    DATE_TIME_FORMATS = DATE_TIME_FORMATS;
    Validators = Validators;
    OTHER_VALUE = OTHER_VALUE;
    OTHER_FIELD_MAX_LENGTH = OTHER_FIELD_MAX_LENGTH;
    DETAILS_MAX_LENGTH = DETAILS_MAX_LENGTH;
    CONTACT_REASON_OTHER_MAX_LENGTH = CONTACT_REASON_OTHER_MAX_LENGTH;
    VICTIM_ACTION_OTHER_MAX_LENGTH = VICTIM_ACTION_OTHER_MAX_LENGTH;
    MAX_LINKS = 10;
    CONTACT_TYPES_WITH_MSISDN = CONTACT_TYPES_WITH_MSISDN;
    CONTACT_TYPES_WITH_SUSPICIOUS_LINKS = CONTACT_TYPES_WITH_SUSPICIOUS_LINKS;
    CONTACT_TYPES_WITH_INLINE_FRAUDSTER_ROLE = CONTACT_TYPES_WITH_INLINE_FRAUDSTER_ROLE;

    get canAddMoreLinks(): boolean {
        return this.contactForm?.controls?.suspiciousLinks?.length < this.MAX_LINKS;
    }

    get canRemoveLink(): boolean {
        return this.contactForm?.controls?.suspiciousLinks?.length > 1;
    }

    get showDetailsTextarea(): boolean {
        const contactType = this.contactForm?.controls?.contactType?.value;
        return contactType && hasDetailsTextarea(contactType);
    }

    get activeTypeSections(): ContactTypeSection[] {
        const type = this.contactForm?.controls?.contactType?.value;
        return type ? (CONTACT_TYPE_SECTIONS[type] ?? []) : [];
    }

    getControl(name: keyof ContactForm): FormControl<string> {
        return this.contactForm.controls[name] as FormControl<string>;
    }
}

interface ContactTypeSection {
    dictionaryName: ReferenceBookType;
    controlName: keyof ContactForm;
    otherControlName: keyof ContactForm;
    label: string;
    placeholder: string;
    otherLabel: string;
    otherPlaceholder: string;
}
